/**
 * NowRoom — the "Now" room in Mission Control.
 *
 * Layout (BRD §7.2):
 *   - Room header with eyebrow + live meta line
 *   - Health banner (non-nominal only)
 *   - Constellation SVG pipeline canvas
 *   - Decisions strip (top 3 pending, link to all)
 *   - Activity ticker (right-to-left scrolling, recent agent actions)
 *
 * Data:
 *   health / agents / recentActions → usePulseBarData (15 s / 30 s / 10 s)
 *   decisions                       → GET /api/admin/ops/decisions (30 s)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, Inbox, Cpu, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";
import { usePulseBarData } from "../hooks/usePulseBarData";
import Constellation from "../constellation/Constellation";

// ── Mobile ambient grid (≤640px replaces constellation SVG) ──────────────────

function AmbientGrid({ counts }) {
  const cells = [
    { label: "Active",    value: counts.active    ?? "—" },
    { label: "Engaged",   value: counts.engaged   ?? "—" },
    { label: "Brief",     value: counts.brief     ?? "—" },
    { label: "Vetting",   value: counts.vetting   ?? "—" },
    { label: "Sourced",   value: counts.sourced   ?? "—" },
    { label: "Returning", value: counts.returning ?? "—" },
  ];
  return (
    <div className="mc-ambient-grid" aria-label="Pipeline counts">
      {cells.map(({ label, value }) => (
        <div key={label} className="mc-ambient-cell">
          <span className="mc-ambient-value">{value}</span>
          <span className="mc-ambient-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── GST date string ───────────────────────────────────────────────────────────

function gstDateString() {
  return new Date().toLocaleDateString("en-GB", {
    timeZone: "Asia/Dubai",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ── Age helper ────────────────────────────────────────────────────────────────

function formatAge(isoString) {
  if (!isoString) return "—";
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Priority derivation ───────────────────────────────────────────────────────

const STAKES_RANK  = { high: 1, medium: 2, low: 3 };
const URGENCY_RANK = { urgent: 1, normal: 2, low: 3 };

function derivePriority(decision) {
  const s = STAKES_RANK[decision.stakes]   || 3;
  const u = URGENCY_RANK[decision.urgency] || 2;
  const score = s + u;
  if (score <= 2) return "P1";
  if (score <= 4) return "P2";
  if (score <= 5) return "P3";
  return "P4";
}

// ── HealthBanner ──────────────────────────────────────────────────────────────

function HealthBanner({ health, healthError }) {
  if (!health && !healthError) return null;
  const status = health?.status;
  if (status === "nominal") return null;

  const label = healthError
    ? "Health check unreachable"
    : status === "degraded"
    ? "Platform degraded — some services are slow"
    : "Incident in progress — check agent feeds";

  const cls =
    healthError || status === "incident"
      ? "mc-now-banner mc-now-banner--incident"
      : "mc-now-banner mc-now-banner--degraded";

  return (
    <div className={cls} role="alert">
      <Activity size={14} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

// ── PriorityTag ───────────────────────────────────────────────────────────────

function PriorityTag({ priority }) {
  const variantClass =
    priority === "P1" ? "mc-priority-tag--p1"
    : priority === "P2" ? "mc-priority-tag--p2"
    : priority === "P3" ? "mc-priority-tag--p3"
    : "mc-priority-tag--p4";
  return <span className={`mc-priority-tag ${variantClass}`}>{priority}</span>;
}

// ── DecisionStripCard ─────────────────────────────────────────────────────────

function DecisionStripCard({ decision }) {
  const priority = derivePriority(decision);
  const title =
    decision.context?.title ||
    decision.context?.subject ||
    `${decision.agent_id} — ${decision.recommendation || "decision pending"}`;
  const optionLabels =
    (decision.options || []).map((o) => o.label || o).slice(0, 3);

  return (
    <Link
      to="/mission-control/decisions"
      className="mc-decision-strip-card"
      title={`Decision: ${title}`}
    >
      <div className="mc-dsc-header">
        <PriorityTag priority={priority} />
        <span className="mc-dsc-agent mc-mono">{decision.agent_id}</span>
        <span className="mc-dsc-age">{formatAge(decision.surfaced_at)}</span>
      </div>
      <div className="mc-dsc-title">{title}</div>
      {optionLabels.length > 0 && (
        <div className="mc-dsc-options">
          {optionLabels.map((label, i) => (
            <span key={i} className="mc-dsc-option-chip">
              {label}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

// ── TickerEntry ───────────────────────────────────────────────────────────────

function TickerEntry({ action }) {
  const time = action.created_at
    ? new Date(action.created_at).toLocaleTimeString("en-US", {
        timeZone: "Asia/Dubai",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—:—";

  const outcomeClass =
    action.outcome === "success" ? "mc-ticker-outcome--success"
    : action.outcome === "failed" ? "mc-ticker-outcome--failed"
    : "";

  return (
    <span className={`mc-ticker-entry ${outcomeClass}`}>
      <span className="mc-ticker-time mc-mono">{time}</span>
      <span className="mc-ticker-sep" aria-hidden="true">·</span>
      <span className="mc-ticker-agent mc-mono">{action.agent_id}</span>
      <span className="mc-ticker-arrow" aria-hidden="true">→</span>
      <span className="mc-ticker-action">{action.action_type}</span>
    </span>
  );
}

// ── NowRoom ───────────────────────────────────────────────────────────────────

export default function NowRoom() {
  const navigate = useNavigate();
  const { health, healthError, pendingDecisions, activeAgents, totalAgents, recentActions } =
    usePulseBarData();

  const [selectedDot, setSelectedDot]     = useState(null);
  const [decisions, setDecisions]         = useState([]);
  const [decisionsLoading, setDecisionsLoading] = useState(true);
  const [gstDate]                         = useState(gstDateString);
  const [pipelineCounts, setPipelineCounts] = useState({});

  // ── Decisions polling ──────────────────────────────────────────────────────

  const fetchDecisions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/admin/ops/decisions?include_decided=false&limit=3`,
        { headers: authHeaders() }
      );
      setDecisions(res.data?.decisions || []);
    } catch {
      // keep stale
    } finally {
      setDecisionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDecisions();
    const t = setInterval(fetchDecisions, 30_000);
    return () => clearInterval(t);
  }, [fetchDecisions]);

  // ── Pipeline counts for mobile ambient grid ────────────────────────────────

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/pipeline-counts`, { headers: authHeaders() });
      setPipelineCounts(res.data || {});
    } catch { /* keep stale */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    const t = setInterval(fetchCounts, 30_000);
    return () => clearInterval(t);
  }, [fetchCounts]);

  // ── Dot selection ──────────────────────────────────────────────────────────

  function handleSelectDot(dot) {
    setSelectedDot(dot);
    window.dispatchEvent(new CustomEvent("mc:dotSelected", { detail: dot }));
  }

  // ── Ticker (newest on right — actions arrive newest-first, reverse for scroll) ─

  const tickerActions = [...(recentActions || [])].reverse().slice(0, 40);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--now" aria-label="Now room">

      {/* Room header */}
      <div className="mc-room-header">
        <span className="mc-room-eyebrow">ROOM · NOW</span>
        <span className="mc-room-meta">
          platform pipeline · live
          <span className="mc-room-meta-sep" aria-hidden="true"> | </span>
          <span className="mc-mono">{gstDate} GST</span>
        </span>
      </div>

      {/* Health banner (non-nominal only) */}
      <HealthBanner health={health} healthError={healthError} />

      {/* Quick-stat row */}
      <div className="mc-now-stats">
        <button
          className="mc-now-stat"
          onClick={() => navigate("/mission-control/decisions")}
          aria-label={`${pendingDecisions} pending decisions`}
        >
          <Inbox size={16} aria-hidden="true" />
          <span className="mc-mono mc-now-stat-value">{pendingDecisions}</span>
          <span className="mc-now-stat-label">pending decisions</span>
        </button>
        <button
          className="mc-now-stat"
          onClick={() => navigate("/mission-control/agents")}
          aria-label={`${activeAgents} of ${totalAgents} agents active`}
        >
          <Cpu size={16} aria-hidden="true" />
          <span className="mc-mono mc-now-stat-value">
            {activeAgents}/{totalAgents}
          </span>
          <span className="mc-now-stat-label">agents active</span>
        </button>
      </div>

      {/* Constellation pipeline canvas (desktop) + ambient grid (mobile) */}
      <div className="mc-room-section mc-room-section--constellation">
        {/* AmbientGrid only visible ≤640px via CSS; Constellation hidden ≤640px */}
        <AmbientGrid counts={pipelineCounts} />
        <Constellation onSelectDot={handleSelectDot} />
        {selectedDot && (
          <div className="mc-selected-dot-info" aria-live="polite">
            <span className="mc-selected-dot-name">{selectedDot.name}</span>
            <span className="mc-selected-dot-status">{selectedDot.status}</span>
            <button
              className="mc-selected-dot-dismiss"
              onClick={() => setSelectedDot(null)}
              aria-label="Dismiss selection"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Decisions strip */}
      <div className="mc-room-section mc-room-section--decisions">
        <div className="mc-decisions-strip">
          {decisionsLoading && decisions.length === 0 ? (
            <div className="mc-decisions-strip-empty">
              <span className="mc-mono" style={{ fontSize: 11, opacity: 0.5 }}>
                loading…
              </span>
            </div>
          ) : decisions.length === 0 ? (
            <div className="mc-decisions-strip-empty">
              <Inbox size={16} style={{ opacity: 0.4 }} />
              <span className="mc-decisions-strip-empty-label">
                No pending decisions
              </span>
            </div>
          ) : (
            decisions.map((d) => (
              <DecisionStripCard key={d.id} decision={d} />
            ))
          )}
          <Link
            to="/mission-control/decisions"
            className="mc-decisions-strip-viewall"
            aria-label="View all decisions"
          >
            view all
            <ChevronRight size={12} aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* Activity ticker */}
      {tickerActions.length > 0 && (
        <div className="mc-room-section mc-room-section--ticker">
          <div
            className="mc-ticker"
            role="log"
            aria-label="Recent agent activity"
            aria-live="polite"
          >
            <div className="mc-ticker-track">
              {tickerActions.map((a) => (
                <TickerEntry key={a.id} action={a} />
              ))}
              {/* Duplicate for seamless CSS scroll loop */}
              {tickerActions.map((a) => (
                <TickerEntry key={`dup-${a.id}`} action={a} />
              ))}
            </div>
          </div>
        </div>
      )}

    </section>
  );
}
